import { CroppedImg } from '@utils/getCroppedImg';
import { GetMomentResponse } from './api/moment';
import { User } from './user';

export enum POST_TYPE {
  RESPONSE = 'Response',
  QUESTION = 'Question',
  NOTE = 'Note',
  MISSION_GROUP = 'MissionGroup',
  COMMENT = 'Comment',
  // 새로 추가된 타입
  SELECT_PERSONA = 'SelectPersona',
  SELECT_INTEREST = 'SelectInterest',
  HIGHLIGHT = 'Highlight',
}

export interface ShareSettings {
  share_with_friends: boolean;
  share_anonymously: boolean;
}

export interface QuestionGroup {
  date: string;
  questions: Question[];
}

export interface Question {
  type: 'Question';
  id: number;
  content: string;
  created_at: string;
  is_admin_question: boolean;
  selected_dates: string[];
  selected: boolean;
}

export interface DailyQuestion extends Question {
  author: string | null;
  author_detail: User | AdminAuthor;
}

export interface ContentsCommon {
  id: number;
  content: string;
  author: string | null;
  author_detail: User;
  like_count: number | null;
  current_user_like_id: number | null;
  current_user_reaction_id_list: {
    id: number;
    emoji: string;
  }[];
  created_at: string;
  updated_at: string;
  pinned?: boolean;
  current_user_pin_id?: number | null;
}

// Comment
export interface Comment extends ContentsCommon {
  is_anonymous: boolean;
  is_private: boolean;
  is_reply: boolean;
  replies: Comment[];
  target_id: number;
  type: 'Comment';
  user_tags: UserTag[];
  like_user_sample: User[];
  like_reaction_user_sample: ReactionUserSample[];
}

export type PrivateComment = Omit<Comment, 'author' | 'author_detail'> &
  Pick<Partial<ContentsCommon>, 'id' | 'author' | 'author_detail'>;

// User tag
export interface UserTag {
  id: number;
  tagged_username: string;
  offset: number;
  length: number;
}

export const isAdminAuthor = (author: User | AdminAuthor): author is AdminAuthor => {
  return (author as AdminAuthor).color_hex !== undefined;
};
// Admin author
export interface AdminAuthor {
  color_hex: string;
}

export interface ReactionUserSample extends User {
  like: boolean;
  reaction: string | null;
}

// Response
export interface Response extends ContentsCommon {
  type: POST_TYPE.RESPONSE;
  comments: Comment[];
  question: DailyQuestion;
  question_id: number;
  image: string | null;
  comment_count: number | null;
  like_user_sample: User[];
  like_reaction_user_sample: ReactionUserSample[];
  current_user_read: boolean;
  is_edited: boolean;
  visibility: PostVisibility[];
}

export interface Note extends ContentsCommon {
  type: POST_TYPE.NOTE;
  images: string[];
  comment_count: number | null;
  like_user_sample: User[];
  like_reaction_user_sample: ReactionUserSample[];
  comments: Comment[];
  current_user_read: boolean;
  is_edited: boolean;
  visibility: PostVisibility;
  share_type?: ShareType;
  mission_id?: number | null;
  mission_prompt?: string | null;
  mission_attempt_number?: number | null;
}

export interface MissionGroupItem {
  type: POST_TYPE.MISSION_GROUP;
  mission_id: number | null;
  mission_prompt: string | null;
  author: string | null;
  author_detail: User;
  created_at: string;
  updated_at: string;
  attempts: Note[];
  category?: string;
}

export type NoteFeedItem = Note | MissionGroupItem;
export type AllPostFeedItem = NoteFeedItem | Response;

export interface NewNoteForm {
  content: string;
  images?: CroppedImg[];
  visibility: PostVisibility[];
  share_type?: ShareType;
  mission_id?: number;
}
// Response list for questions
/** deprecated */
export interface DayQuestion extends Omit<Question, 'selected_dates' | 'selected'> {
  responses: QuestionResponse[];
}

export type QuestionResponse = Omit<ContentsCommon, 'author_detail' | 'author'> &
  ShareSettings & {
    type: POST_TYPE.RESPONSE;
    question_id: number;
    author?: string;
  };

export interface MomentPost extends GetMomentResponse {}

export type ResponseDetail = Response & ShareSettings;

export type Reaction = {
  id: number;
  emoji: string;
  user: User;
};

export type ReactionPostType = 'Note' | 'Response' | 'Comment';

export interface ActorDetail {
  id: number;
  username: string;
  profile_image: string | null;
  profile_pic: string;
}

export type Like = {
  id: number;
  target_id: number;
  target_type: POST_TYPE;
  type: 'Like';
  user: string;
  user_detail: ActorDetail;
};

export type PostReaction = {
  id: number;
  type: 'Reaction' | 'Like';
  user: string;
  user_detail: ActorDetail;
  reaction: string | null;
};

export type POST_DP_TYPE = 'LIST' | 'DETAIL' | 'FEED';

export type RecentPost = {
  id: number;
  type: POST_TYPE.NOTE | POST_TYPE.RESPONSE;
  created_at: string;
  current_user_like_id: number | null;
  current_user_reaction_id_list: {
    id: number;
    emoji: string;
  }[];
  preview_content: string;
  is_read: boolean;
  like_reaction_user_sample: ReactionUserSample[];
  images: string[];
  comment_count: number | null;
};

export enum ShareType {
  REGULAR = 'regular',
  TMI_OF_THE_DAY = 'tmi_of_the_day',
  PHOTO_OF_THE_DAY = 'photo_of_the_day',
  MISSION = 'mission',
}

export enum PostVisibility {
  ONLY_ME = 'only_me',
  CLOSE_FRIENDS = 'close_friends',
  FRIENDS = 'friends',
  PUBLIC = 'public',
}

export interface SelectInterest extends ContentsCommon {
  type: POST_TYPE.SELECT_INTEREST;
}

export interface SelectPersona extends ContentsCommon {
  type: POST_TYPE.SELECT_PERSONA;
}

export interface Highlight extends ContentsCommon {
  type: POST_TYPE.HIGHLIGHT;
}
